import { Avatar, Button, Tooltip, Typography } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { ResourceHeaderProps } from 'react-big-calendar';

type EmployeeResource = {
  resourceId: string;
  resourceTitle: string;
  color?: string;
  profileImage?: string;
  initials?: string;
};

type ResourceTitleProps = ResourceHeaderProps<EmployeeResource> & {
  setSelectedArtists: Dispatch<SetStateAction<string[]>>;
  // Shows the column-header avatar; classic mode is untouched.
  redesignActive?: boolean;
};

function ResourceTitle({
  label,
  resource,
  setSelectedArtists,
  redesignActive,
}: ResourceTitleProps) {
  const handleClick = () => {
    setSelectedArtists([resource.resourceId]);
  };

  const showAvatar =
    redesignActive && (resource.profileImage || resource.initials);

  return (
    <Button
      variant="text"
      sx={{
        py: 1,
        mt: 1,
        mb: showAvatar ? 0 : 1,
        px: showAvatar ? 1.5 : 0,
        ...(showAvatar && {
          borderRadius: 0,
          borderBottom: '3px solid',
          borderColor: resource.color || '#333',
        }),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: showAvatar ? 'flex-start' : 'center',
        gap: 1,
      }}
      onClick={handleClick}>
      {showAvatar && (
        <Avatar
          src={resource.profileImage}
          sx={{
            width: 24,
            height: 24,
            fontSize: 10,
            fontWeight: 700,
            bgcolor: resource.color || '#333',
            color: 'common.white',
          }}>
          {resource.initials}
        </Avatar>
      )}
      <Tooltip title={label} disableInteractive>
        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            color: 'text.primary',
            ...(showAvatar && { fontWeight: 700, fontSize: 12 }),
          }}>
          {label}
        </Typography>
      </Tooltip>
    </Button>
  );
}

export const withSelectedArtists =
  (
    setSelectedArtists: Dispatch<SetStateAction<string[]>>,
    redesignActive?: boolean,
  ) =>
  // eslint-disable-next-line react/function-component-definition
  (props: ResourceHeaderProps<EmployeeResource>) => (
    <ResourceTitle
      {...props}
      setSelectedArtists={setSelectedArtists}
      redesignActive={redesignActive}
    />
  );

export default ResourceTitle;
